import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Building, User, LogOut, Settings, Home, Calendar, BarChart3, Bell, LifeBuoy, Layers } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";

export default function Header() {
  const [user, setUser] = useState<any>(null);
  const [userType, setUserType] = useState<'customer' | 'company' | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, formatDate } = useI18n();
  const [unread, setUnread] = useState<number>(0);
  const [notifs, setNotifs] = useState<any[]>([]);
  const [openNotif, setOpenNotif] = useState<boolean>(false);

  useEffect(() => {
    checkUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser(session.user);
        checkUserType(session.user.id);
        loadNotifs();
      } else {
        setUser(null);
        setUserType(null);
        setNotifs([]);
        setUnread(0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setUser(session.user);
      checkUserType(session.user.id);
      loadNotifs();
    }
  };

  const checkUserType = async (userId: string) => {
    const { data: profile } = await supabase
      .from("profiles")
      .select("user_type")
      .eq("user_id", userId)
      .single();
    
    setUserType(profile?.user_type || 'customer');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
    toast({
      title: t('header.toast.signed_out_title', 'Signed out'),
      description: t('header.toast.signed_out_desc', 'You have been successfully signed out.'),
    });
  };

  const handleCompanyRegistration = () => {
    navigate("/company/onboarding");
  };

  const handleCustomerRegistration = () => {
    navigate("/auth?type=customer");
  };

  const handleDashboard = () => {
    if (userType === 'company') {
      navigate("/company/dashboard");
    } else {
      navigate("/dashboard");
    }
  };

  const handleProfile = () => {
    if (userType === 'company') {
      navigate("/company/profile");
    } else {
      navigate("/profile");
    }
  };

  const handleSupport = () => {
    navigate("/support");
  };

  const loadNotifs = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    // Prefer soft-delete filter when column exists (migration 20250917193000_notifications_soft_delete.sql).
    // Preview DBs missing that migration return 400 if we select/filter deleted_at — fall back without it.
    let res = await supabase
      .from('notifications')
      .select('id, title, body, read_at, created_at, link, booking_id, booking_number, deleted_at')
      .eq('user_id', session.user.id)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(10);
    if (res.error) {
      res = await supabase
        .from('notifications')
        .select('id, title, body, read_at, created_at, link, booking_id, booking_number')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(10);
    }
    if (res.error) {
      res = await supabase
        .from('notifications')
        .select('id, title, body, read_at, created_at, link, booking_id')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(10);
    }
    if (res.error) {
      res = await supabase
        .from('notifications')
        .select('id, title, body, read_at, created_at')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(10);
    }
    const data = res.data;
    const rows = (data || []).filter((n: any) => n.deleted_at == null);
    if (!res.error) {
      setNotifs(rows);
      setUnread(rows.filter(n => !n.read_at).length);
    }
  };

  // Refresh when dropdown is open (15 s), plus a quiet background poll to keep the badge current
  useEffect(() => {
    const id = setInterval(() => { if (openNotif) loadNotifs(); }, 15000);
    return () => clearInterval(id);
  }, [openNotif]);

  useEffect(() => {
    const id = setInterval(() => { if (!openNotif) loadNotifs(); }, 60000);
    return () => clearInterval(id);
  }, [openNotif]);

  const markAllRead = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    await supabase.from('notifications').update({ read_at: new Date().toISOString() as any }).is('read_at', null).eq('user_id', session.user.id);
    loadNotifs();
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/88 text-slate-950 shadow-[0_16px_42px_-30px_rgba(15,23,42,0.35)] backdrop-blur-2xl supports-[backdrop-filter]:bg-white/72">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="px-2 text-xl font-bold text-slate-950 hover:bg-slate-100/80 hover:text-slate-950 sm:px-4 sm:text-2xl"
            >
              {t('brand.name','Halomora')}
            </Button>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/")}
              className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
            >
              <Home className="h-4 w-4 mr-2" />
              {t('nav.home','Home')}
            </Button>
            {userType === 'company' && (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/company/dashboard")}
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  {t('nav.dashboard','Dashboard')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/company/dashboard?tab=tracker")}
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                >
                  <Layers className="h-4 w-4 mr-2" />
                  {t('nav.tracker','Tracker')}
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/company/profile")}
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t('nav.profile','Profile')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleSupport}
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                >
                  <LifeBuoy className="h-4 w-4 mr-2" />
                  {t('nav.support','Supporto')}
                </Button>
              </>
            )}
            {userType === 'customer' && (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/dashboard")}
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  {t('nav.bookings','My Bookings')}
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/profile")}
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                >
                  <User className="h-4 w-4 mr-2" />
                  {t('nav.profile','Profile')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleSupport}
                  className="text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                >
                  <LifeBuoy className="h-4 w-4 mr-2" />
                  {t('nav.support','Supporto')}
                </Button>
              </>
            )}
          </nav>

          {/* Language Switch + Auth Section */}
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            {user ? (
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Button variant="ghost" className="relative text-slate-700 hover:bg-slate-100 hover:text-slate-950" onClick={() => { setOpenNotif(v => !v); if (!openNotif) loadNotifs(); }}>
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] leading-none rounded-full px-1.5 py-0.5">{unread}</span>
                    )}
                  </Button>
                  {/* Dropdown */}
                  {openNotif && (
                  <div className="absolute right-0 mt-2 w-72 rounded-[1.25rem] border border-slate-200/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(248,250,252,0.92)_100%)] p-2 text-slate-900 shadow-panel backdrop-blur-2xl">
                    <div className="flex items-center justify-between px-2 py-1">
                      <div className="text-sm font-medium">{t('nav.notifications','Notifications')}</div>
                      <Button variant="ghost" size="sm" onClick={markAllRead}>{t('nav.mark_all_read','Mark all read')}</Button>
                    </div>
                    <div className="max-h-80 overflow-auto">
                      {notifs.length === 0 ? (
                        <div className="px-2 py-3 text-xs text-slate-500">{t('nav.no_notifications', 'No notifications')}</div>
                      ) : notifs.map(n => (
                        <div key={n.id} className={`rounded-[1rem] border px-3 py-3 ${n.read_at ? 'border-slate-100 bg-white/80' : 'border-primary/15 bg-primary/5'}`}>
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-slate-950">{n.title}</div>
                            <div className="text-xs text-slate-600">{n.body}</div>
                            {n.booking_number && (
                              <div className="text-xs text-primary font-medium">
                                {t('nav.booking_ref','Prenotazione')}: {n.booking_number} • {formatDate(n.created_at)}
                              </div>
                            )}
                            <div className="flex items-center gap-2 pt-2">
                              {!n.read_at && (
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={async () => { await supabase.from('notifications').update({ read_at: new Date().toISOString() as any }).eq('id', n.id); loadNotifs(); }}>
                                  {t('nav.mark_read','Segna come letto')}
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={async () => {
                                await supabase.from('notifications').update({ deleted_at: new Date().toISOString() as any }).eq('id', n.id);
                                loadNotifs();
                              }}>
                                {t('nav.delete','Elimina')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
                <Badge 
                  variant={userType === 'company' ? 'default' : 'secondary'}
                  className="px-3 py-1"
                >
                  {userType === 'company' ? (
                    <>
                      <Building className="h-3 w-3 mr-1" />
                      {t('nav.company','Company')}
                    </>
                  ) : (
                    <>
                      <User className="h-3 w-3 mr-1" />
                      {t('nav.customer','Customer')}
                    </>
                  )}
                </Badge>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full border border-slate-200/80 bg-white/80 hover:bg-white">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-primary text-white font-medium">
                          {user.email?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuItem 
                      onClick={handleDashboard}
                      className="cursor-pointer"
                    >
                      <BarChart3 className="mr-2 h-4 w-4" />
                      <span>{t('nav.dashboard','Dashboard')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleProfile}
                      className="cursor-pointer"
                    >
                      <User className="mr-2 h-4 w-4" />
                      <span>{t('nav.profile','Profile')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleSupport}
                      className="cursor-pointer"
                    >
                      <LifeBuoy className="mr-2 h-4 w-4" />
                      <span>{t('nav.support','Supporto')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      className="cursor-pointer text-red-600 hover:text-red-700"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('nav.sign_out','Sign out')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center space-x-1 sm:space-x-3">
                <Button 
                  variant="outline" 
                  onClick={handleCustomerRegistration}
                  className="h-8 px-2 py-1.5 text-xs sm:h-10 sm:px-3 sm:py-2 sm:text-sm"
                >
                  <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{t('nav.sign_in','Sign In')}</span>
                  <span className="sm:hidden">{t('nav.sign_in_short','Accedi')}</span>
                </Button>
                <Button 
                  onClick={handleCompanyRegistration}
                  className="h-8 whitespace-nowrap px-2 py-1.5 text-xs sm:h-10 sm:px-3 sm:py-2 sm:text-sm"
                >
                  <Building className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{t('nav.join_company','Join as Company')}</span>
                  <span className="sm:hidden">{t('nav.join_company_short','Azienda')}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
} 